import { IKeyword } from "@/utilities/types/Keyword";
import React, { useEffect, useState } from "react";
import { Input, Button } from "rizzui";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  FormKeywords,
  formKeywordsSchema,
} from "@/utils/validators/keywords.schema";

interface KeywordInputGroupProps {
  onSave: (pairs: IKeyword[]) => Promise<void>;
  initialData: IKeyword[];
  refetch: () => Promise<void>;
}

const KeywordInputGroup: React.FC<KeywordInputGroupProps> = ({
  onSave,
  initialData = [],
  refetch,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormKeywords>({
    resolver: zodResolver(formKeywordsSchema),
    defaultValues: { keywords: [...initialData, { name: "", value: "" }] },
    mode: "onChange",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "keywords",
  });

  useEffect(() => {
    reset({ keywords: [...initialData, { name: "", value: "" }] });
  }, [initialData, reset]);

  const onSubmit = async (values: FormKeywords) => {
    setIsSubmitting(true);
    await onSave(
      values.keywords.map((k, idx) => ({
        name: k.name ?? "",
        value: k.value ?? "",
        ...(fields[idx]?.id ? { id: fields[idx].id } : {}),
      }))
    );
    await refetch();
    setIsSubmitting(false);
  };

  return (
    <form
      className="space-y-4 mb-6"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
    >
      {fields.map((field, i) => (
        <div
          key={field.id || i}
          className="flex flex-col sm:flex-row gap-3 items-start"
        >
          <Controller
            control={control}
            name={`keywords.${i}.name`}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                placeholder="e.g., Luxury Penthouse"
                className="flex-1 w-full"
                error={fieldState.error?.message}
              />
            )}
          />
          <div className="flex-1 w-full flex flex-col">
            <Controller
              control={control}
              name={`keywords.${i}.value`}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  placeholder="e.g., /luxury-penthouse"
                  className="w-full"
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              color="danger"
              onClick={() => remove(i)}
              className="whitespace-nowrap"
              disabled={
                isSubmitting ||
                (fields.length === 1 && fields[i].name === "" && fields[i].value === "")
              }
              type="button"
            >
              Remove
            </Button>
            {i === fields.length - 1 && (
              <Button
                variant="outline"
                onClick={() => append({ name: "", value: "" })}
                className="whitespace-nowrap"
                disabled={isSubmitting}
                type="button"
              >
                + Add More
              </Button>
            )}
          </div>
        </div>
      ))}

      <Button
        type="submit"
        variant="solid"
        color="primary"
        className="mt-2"
        isLoading={isSubmitting}
        disabled={
          !isDirty ||
          (Array.isArray(errors.keywords) && errors.keywords.length > 0)
        }
      >
        Save Keywords
      </Button>
    </form>
  );
};

export default KeywordInputGroup;
